import { useEffect, useRef, useState } from "react";
import type { Summary } from "./galleryTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { useAuthedAssetURL } from "./useAuthedAssetURL";
import { apiPath, useApiResource } from "./useApiResource";

// BitThumb only exists to isolate the useAuthedAssetURL call — mounting it
// conditionally (once a tile is visible) is how BitTile makes thumbnail
// fetching lazy, since hooks can't be called conditionally within one
// component.
function BitThumb({ id, token, repo }: { id: string; token: string; repo: string }) {
  const { url } = useAuthedAssetURL(apiPath(repo, `/bits/${encodeURIComponent(id)}/thumbnail`), token);
  return url ? <img src={url} alt="" /> : null;
}

// BitTile fetches its thumbnail only once it scrolls near the viewport
// (IntersectionObserver, 200px rootMargin so it starts loading just before
// it's visible). Real production incident: eagerly fetching all ~146 tiles
// on mount fired that many concurrent thumbnail requests and OOM-killed the
// app's 512MB instance (see CLAUDE.md) — this is the client-side half of
// the fix; internal/gallery.Store also caps concurrent server-side
// fetch/decode work as defense in depth.
function BitTile({ item, token, repo, count }: { item: Summary; token: string; repo: string; count: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setVisible(true);
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible]);

  return (
    <div ref={ref} className="bit-tile" tabIndex={0} onClick={() => navigate(repoPath(repo, "bits", item.id))}>
      <div className="bit-thumb">{visible && <BitThumb id={item.id} token={token} repo={repo} />}</div>
      <p className="bit-title">{item.title}</p>
      <p className="muted-note">
        {item.ext.replace(".", "").toUpperCase()}
        {item.isDraft ? " · Draft" : ""}
        {item.isWip ? " · WIP" : ""}
        {count > 1 ? ` · ${count} revisions` : ""}
      </p>
    </div>
  );
}

// BitsGallery is the spec §6.5 B&IT gallery: a thumbnail grid over the
// whole (~146-item) GET /api/bits response, the same "whole small dataset
// in the browser" pattern as every other index in this app. File-type,
// sector, and draft/WIP filters plus free-text search are client-side.
// Related ADRs/capabilities and tags are omitted (see internal/gallery's
// package doc comment — no groundable signal exists for either in the real
// corpus); "Favourites"/"Recently viewed"/"Side-by-side comparison" are
// §6.5's broader wishlist, not part of M10's own deliverable list (spec
// §28: Thumbnails, Zoom, Full screen, Revision grouping, Metadata, Related
// content) and are not attempted here.
export function BitsGallery({ token }: { token: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Summary[]>(token, repo, "/bits");
  const items = data ?? [];
  const [query, setQuery] = useState("");
  const [ext, setExt] = useState("");
  const [sector, setSector] = useState("");
  const [draftOnly, setDraftOnly] = useState(false);
  const [wipOnly, setWipOnly] = useState(false);

  if (status === "loading") {
    return (
      <section className="panel">
        <h2>B&amp;IT gallery</h2>
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready") {
    return (
      <section className="panel">
        <h2>B&amp;IT gallery</h2>
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const groupCounts = new Map<string, number>();
  for (const it of items) {
    if (it.revisionGroup) groupCounts.set(it.revisionGroup, (groupCounts.get(it.revisionGroup) ?? 0) + 1);
  }

  const extOptions = [...new Set(items.map((i) => i.ext))].sort();
  const sectorOptions = [...new Set(items.map((i) => i.sector).filter(Boolean))].sort();

  const needle = query.trim().toLowerCase();
  const filtered = items.filter((it) => {
    if (ext && it.ext !== ext) return false;
    if (sector && it.sector !== sector) return false;
    if (draftOnly && !it.isDraft) return false;
    if (wipOnly && !it.isWip) return false;
    if (needle && !`${it.title} ${it.sourcePath}`.toLowerCase().includes(needle)) return false;
    return true;
  });

  return (
    <section className="panel">
      <h2>B&amp;IT gallery</h2>
      <div className="register-toolbar">
        <input
          type="search"
          role="searchbox"
          aria-label="Search B&IT items"
          placeholder="Search title, path…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <label>
          File type{" "}
          <select aria-label="File type" value={ext} onChange={(e) => setExt(e.target.value)}>
            <option value="">All types</option>
            {extOptions.map((e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ))}
          </select>
        </label>
        {sectorOptions.length > 0 && (
          <label>
            Sector{" "}
            <select aria-label="Sector" value={sector} onChange={(e) => setSector(e.target.value)}>
              <option value="">All sectors</option>
              {sectorOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        )}
        <label>
          <input type="checkbox" checked={draftOnly} onChange={(e) => setDraftOnly(e.target.checked)} />
          Draft only
        </label>
        <label>
          <input type="checkbox" checked={wipOnly} onChange={(e) => setWipOnly(e.target.checked)} />
          WIP only
        </label>
      </div>
      <p className="muted-note">
        {filtered.length} of {items.length} items
      </p>
      <div className="bit-grid">
        {filtered.map((item) => (
          <BitTile key={item.id} item={item} token={token} repo={repo} count={groupCounts.get(item.revisionGroup) ?? 0} />
        ))}
      </div>
    </section>
  );
}
